import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EarningsChart from './components/EarningsChart';
import TransactionTable from './components/TransactionTable';
import PayoutStatus from './components/PayoutStatus';
import WithdrawalMethods from './components/WithdrawalMethods';
import StatsCard from '../../pages/user-dashboard/components/StatsCard';
import { 
  getTransactions, 
  getEarningsSummary, 
  getPayouts,
  getWithdrawalMethods,
  getPayoutSchedule 
} from '../../services/earningsService';

const Earnings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({
    totalEarnings: 0,
    totalWithdrawals: 0,
    currentBalance: 0,
    monthlyData: []
  });
  const [payouts, setPayouts] = useState([]);
  const [withdrawalMethods, setWithdrawalMethods] = useState([]);
  const [payoutSchedule, setPayoutSchedule] = useState({ nextPayout: null, pendingAmount: 0 });
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchEarningsData();
  }, []);

  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [transData, summaryData, payoutsData, methodsData, scheduleData] = await Promise.all([
        getTransactions({ limit: 50 }),
        getEarningsSummary(),
        getPayouts({ limit: 10 }),
        getWithdrawalMethods(),
        getPayoutSchedule()
      ]);

      setTransactions(transData || []);
      setSummary(summaryData || { totalEarnings: 0, totalWithdrawals: 0, currentBalance: 0, monthlyData: [] });
      setPayouts(payoutsData || []);
      setWithdrawalMethods(methodsData || []);
      setPayoutSchedule(scheduleData || { nextPayout: null, pendingAmount: 0 });
    } catch (err) {
      console.error('Error fetching earnings data:', err);
      setError(err?.message || 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    try {
      const filteredTransactions = await getTransactions({ ...newFilters, limit: 50 });
      setTransactions(filteredTransactions || []);
    } catch (err) {
      console.error('Error filtering transactions:', err);
    }
  };

  const stats = [
    {
      icon: 'DollarSign',
      label: 'Total Earnings',
      value: '₱' + summary?.totalEarnings?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      trend: 'up',
      trendValue: '+12%',
      color: 'primary'
    },
    {
      icon: 'Wallet',
      label: 'Current Balance',
      value: '₱' + summary?.currentBalance?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      trend: 'up',
      trendValue: '+8%',
      color: 'success'
    },
    {
      icon: 'TrendingUp',
      label: 'Total Withdrawals',
      value: '₱' + summary?.totalWithdrawals?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      trend: 'up',
      trendValue: '+5%',
      color: 'warning'
    },
    {
      icon: 'Clock',
      label: 'Pending Payouts',
      value: '₱' + payoutSchedule?.pendingAmount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      trend: null,
      trendValue: null,
      color: 'accent'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'transactions', label: 'Transactions', icon: 'Receipt' },
    { id: 'payouts', label: 'Payouts', icon: 'Send' },
    { id: 'methods', label: 'Withdrawal Methods', icon: 'CreditCard' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading earnings data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Earnings</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={fetchEarningsData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Earnings - Diskarte</title>
        <meta name="description" content="Manage your earnings, track transactions, and handle payouts" />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
            Earnings Management
          </h1>
          <p className="text-muted-foreground">
            Track your income, manage payouts, and optimize your financial workflows
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {stats?.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        {/* Tabs Navigation */}
        <div className="bg-card rounded-lg shadow-warm mb-6 md:mb-8">
          <div className="flex overflow-x-auto border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-medium text-sm md:text-base whitespace-nowrap transition-smooth ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              <EarningsChart monthlyData={summary?.monthlyData || []} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PayoutStatus 
                  payouts={payouts?.slice(0, 5) || []} 
                  nextPayout={payoutSchedule?.nextPayout}
                  onRefresh={fetchEarningsData}
                />
                <TransactionTable 
                  transactions={transactions?.slice(0, 10) || []} 
                  showFilters={false}
                  compact={true}
                />
              </div>
            </>
          )}

          {activeTab === 'transactions' && (
            <TransactionTable 
              transactions={transactions || []} 
              showFilters={true}
              filters={filters}
              onFilterChange={handleFilterChange}
              onRefresh={fetchEarningsData}
            />
          )}

          {activeTab === 'payouts' && (
            <PayoutStatus 
              payouts={payouts || []} 
              nextPayout={payoutSchedule?.nextPayout}
              withdrawalMethods={withdrawalMethods || []}
              onRefresh={fetchEarningsData}
              fullView={true}
            />
          )}

          {activeTab === 'methods' && (
            <WithdrawalMethods 
              methods={withdrawalMethods || []} 
              onRefresh={fetchEarningsData}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default Earnings;